import { useState } from "react";

import { Box, FormControlLabel, MenuItem, Select, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import Switch from '@mui/material/Switch';
import { LineChart } from "@mui/x-charts/LineChart";

import { DataLayout } from "./DataLayout";
import { formatCurrency, getYearRangeOptions } from "../utils";

interface BudgetChartProps {
    budgetData: any[] | undefined;
    year?: number;
    onYearChange?: (year: number) => void;
}

export const BudgetChart: React.FC<BudgetChartProps> = ({ budgetData, year, onYearChange }) => {

    const theme = useTheme();
    const [cumulative, setCumulative] = useState(true);
    const [selectedBudgets, setSelectedBudgets] = useState<string[]>([]);
    const yearOptions = getYearRangeOptions();

    if (!budgetData || budgetData.length === 0) return null;

    // All budget keys dynamically
    const BUDGET_KEYS = Object.keys(budgetData[0]).filter((k) =>
        k.startsWith("Budget")
    );

    // Line chart keys
    const LINE_KEYS = [
        { key: "Actual", color: "#1E90FF", order: 1 },
        { key: "Forecast", color: "#32CD32", order: 2 },
        { key: "Baseline", color: "#FFA500", order: 3 },
        { key: "LBE", color: "#8A2BE2", order: 4 },
        // { key: "EAC", color: "#8A2BE2", order: 4 },
        // { key: "Commitments", color: "#FF1493", order: 5 },
        // { key: "Revised", color: "#00CED1", order: 6 },
        // { key: "Variation", color: "#FF4500", order: 7 },
    ];

    // Toggle legend selection
    const toggleBudget = (key: string) => {
        setSelectedBudgets((prev) => {
            if (prev.includes(key)) {
                return prev.filter((k) => k !== key); // deselect
            } else {
                return [...prev, key]; // select
            }
        });
    };

    // If no selection → show all budgets
    const visibleBudgets =
        selectedBudgets.length === 0 ? BUDGET_KEYS : selectedBudgets;

    const xLabels = budgetData.map((item) => item.month);

    const budgetSeries = BUDGET_KEYS.map((key, idx) => {
        const isVisible = visibleBudgets.includes(key);
        const color =
            theme.palette.secondary[
            ["light", "main", "dark"][idx % 3] as keyof typeof theme.palette.secondary
            ];

        return {
            label: key,
            data: budgetData.map((item) => (isVisible ? item[key] : 0)),
            color: isVisible ? color : "#d3d3d3",
            showMark: false,
        };
    });

    const lineSeries = LINE_KEYS.sort((a, b) => a.order - b.order).map(({ key, color }) => ({
        label: key,
        data: budgetData.map((item) => item[key]?.[cumulative ? "cumulative" : "value"] ?? 0),
        color,
        showMark: false,
    }));

    return (
        <DataLayout
            title="Project S-Curve & Cash Flow Forecast"
            action={
                <Box sx={{display: "flex", justifyContent:"center", alignItems: "center", gap: 2}}>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={cumulative}
                            onChange={() => setCumulative(!cumulative)}
                        />
                    }
                    label="Cumulative"
                />
                <Select
                    size="small"
                    value={year ?? new Date().getFullYear()}
                    onChange={(e) => onYearChange?.(Number(e.target.value))}
                >
                    {yearOptions.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                </Select>
                </Box>
            }
        >
            <LineChart
                xAxis={[{ scaleType: "point", data: xLabels }]}
                yAxis={[{ valueFormatter: (value: number) => formatCurrency(value) }]}
                series={[...budgetSeries, ...lineSeries]}
                height={330}
                margin={{ top: 20, right: 70, left: 0, bottom: 40 }}
                grid={{ vertical: true, horizontal: true }}
            />
        </DataLayout>
    );
};